import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Typography } from '@/components/ui/typography';

export type ActionButtonEditDialogValues = {
  label: string;
  link: string;
  backgroundColor: string;
};

export function ActionButtonEditDialog({
  open,
  onOpenChange,
  defaultValues,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultValues: ActionButtonEditDialogValues;
  onSubmit: (values: ActionButtonEditDialogValues) => void;
}) {
  const intl = useIntl();
  const { register, handleSubmit, reset, watch, setValue } =
    useForm<ActionButtonEditDialogValues>({
      defaultValues,
      mode: 'onChange',
    });

  // When switching between buttons, update the form values.
  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [defaultValues, open, reset]);

  const backgroundColor = watch('backgroundColor');

  const handleActionButtonFormSubmit = (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.stopPropagation();
    void handleSubmit(values => {
      onSubmit(values);
      onOpenChange(false);
    })(e);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              defaultMessage="Manage button"
              description="Title of the action button edit dialog"
            />
          </DialogTitle>
        </DialogHeader>

        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={handleActionButtonFormSubmit}
        >
          <DialogBody className="flex flex-col gap-4 p-4">
            <Input
              label={intl.formatMessage({
                defaultMessage: 'Label',
                description:
                  'Form field: text shown on the in-message CTA button',
              })}
              placeholder={intl.formatMessage({
                defaultMessage: 'Apply to job',
                description: 'Example CTA text for a recruiting message button',
              })}
              autoFocus
              {...register('label')}
            />

            <div className="flex items-end gap-3">
              <div className="flex w-full flex-col gap-1">
                <Typography variant="subtitle2">
                  <FormattedMessage
                    defaultMessage="Background color"
                    description="Color picker for the CTA button background in rich text"
                  />
                </Typography>
                <div className="flex items-center gap-1">
                  <input
                    aria-label="Background color picker"
                    type="color"
                    value={backgroundColor}
                    onChange={e =>
                      setValue('backgroundColor', e.target.value, {
                        shouldDirty: true,
                      })
                    }
                    className="h-8 w-9 cursor-pointer overflow-hidden rounded-sm border-0 p-0 [appearance:none] [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-sm [&::-webkit-color-swatch]:border-0 [&::-moz-color-swatch]:rounded-sm [&::-moz-color-swatch]:border-0"
                  />
                  <Input
                    placeholder="#2600FF"
                    {...register('backgroundColor')}
                  />
                </div>
              </div>
            </div>

            <Input
              label={intl.formatMessage({
                defaultMessage: 'Link to',
                description:
                  'Read-only: destination URL the CTA opens (often pre-filled)',
              })}
              placeholder="https://applyhere.com"
              {...register('link')}
              disabled
            />
          </DialogBody>

          <DialogFooter>
            <Button type="submit">
              <FormattedMessage
                defaultMessage="Confirm"
                description="Submit: save CTA label, color, and link to the message"
              />
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
